import { useCallback, useMemo, useState } from "react";
import { applyDataTable, hasActiveColumnFilters } from "../lib/dataTable";

/**
 * Client-side table state: per-column filters + single-column sort.
 *
 * @param {object[]} data
 * @param {import('../lib/dataTable').DataTableColumn[]} columns
 * @param {{ id: string, desc?: boolean } | null} [defaultSort]
 */
export function useDataTable(data, columns, defaultSort = null, options = {}) {
  const { serverSide = false } = options;
  const [columnFilters, setColumnFilters] = useState({});
  const [sorting, setSorting] = useState(defaultSort);

  const rows = useMemo(() => {
    if (serverSide) return data;
    return applyDataTable(data, columns, columnFilters, sorting);
  }, [data, columns, columnFilters, sorting, serverSide]);

  const setColumnFilter = useCallback((columnId, value) => {
    setColumnFilters((prev) => {
      const next = { ...prev };
      if (value == null || value === "") delete next[columnId];
      else next[columnId] = value;
      return next;
    });
  }, []);

  const clearColumnFilter = useCallback((columnId) => {
    setColumnFilters((prev) => {
      const next = { ...prev };
      delete next[columnId];
      return next;
    });
  }, []);

  const clearAllFilters = useCallback(() => setColumnFilters({}), []);

  const getColumnFilter = useCallback((columnId) => columnFilters[columnId] ?? "", [columnFilters]);

  const isColumnFiltered = useCallback(
    (columnId) => Boolean(columnFilters[columnId]),
    [columnFilters],
  );

  const isColumnSorted = useCallback((columnId) => sorting?.id === columnId, [sorting]);

  const setColumnSort = useCallback((columnId, desc) => {
    setSorting({ id: columnId, desc: Boolean(desc) });
  }, []);

  const toggleColumnSort = useCallback(
    (columnId, defaultDesc = false) => {
      setSorting((prev) => {
        if (prev?.id === columnId) return { id: columnId, desc: !prev.desc };
        return { id: columnId, desc: defaultDesc };
      });
    },
    [],
  );

  const clearColumnSort = useCallback(() => setSorting(null), []);

  const resetTable = useCallback(() => {
    setColumnFilters({});
    setSorting(defaultSort ?? null);
    // defaultSort intentionally omitted from deps — only used on explicit reset
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const table = useMemo(
    () => ({
      rows,
      sorting,
      columnFilters,
      setColumnFilter,
      clearColumnFilter,
      clearAllFilters,
      resetTable,
      getColumnFilter,
      isColumnFiltered,
      isColumnSorted,
      setColumnSort,
      toggleColumnSort,
      clearColumnSort,
      hasActiveFilters: hasActiveColumnFilters(columnFilters) || Boolean(sorting),
    }),
    [
      rows,
      sorting,
      columnFilters,
      defaultSort,
      setColumnFilter,
      clearColumnFilter,
      clearAllFilters,
      resetTable,
      getColumnFilter,
      isColumnFiltered,
      isColumnSorted,
      setColumnSort,
      toggleColumnSort,
      clearColumnSort,
    ],
  );

  return table;
}
